import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { FeatureToggle } from '../../components/ui/FeatureToggle'
import { ImageUploadField } from '../../components/ui/ImageUploadField'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { SearchableMultiSelect, coerceStringArray } from '../../components/ui/SearchableMultiSelect'
import { SkeletonStackedCards, SkeletonTable } from '../../components/ui/Skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/Table'
import * as gateways from '../../services/paymentGatewayService'
import { APP_NAME } from '../../config/brand'
import type { PlatformPaymentGatewayRow } from '../../services/paymentGatewayService'
import { PAYMENT_GATEWAY_CURRENCIES } from '../../utils/currencies'

/** Keep in sync with `PLATFORM_GATEWAY_PROVIDERS` in shopora_Api (`platformPaymentGateway.admin.dto`). */
const CHECKOUT_PROVIDERS = ['paystack', 'mollie', 'stripe'] as const

type DeactivateMode = 'table' | 'edit'

type FormState = {
  displayName: string
  provider: string
  logoUrl: string
  sortOrder: string
  isGloballyActive: boolean
  secretKey: string
  publicKey: string
  supportedCurrencies: string[]
}

const emptyForm: FormState = {
  displayName: '',
  provider: 'paystack',
  logoUrl: '',
  sortOrder: '0',
  isGloballyActive: true,
  secretKey: '',
  publicKey: '',
  supportedCurrencies: [],
}

function gatewayStatusBadge(active: boolean) {
  const base =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset'
  if (active) {
    return (
      <span className={`${base} bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-400`}>
        Active
      </span>
    )
  }
  return (
    <span className={`${base} bg-muted text-muted-foreground ring-border`}>Inactive</span>
  )
}

function validateForm(form: FormState, _editing: PlatformPaymentGatewayRow | null): string | null {
  if (!form.displayName.trim()) return 'Display name is required'
  if (form.supportedCurrencies.length === 0) return 'Select at least one supported currency'
  return null
}

function DeactivateConfirmBody({ target }: { target: PlatformPaymentGatewayRow }) {
  return (
    <div className="space-y-3 text-sm text-muted-foreground">
      <p>
        <span className="font-medium text-foreground">{target.displayName}</span> will no longer be offered at
        checkout for matching store currencies.
      </p>
      {(target.enabledStoreCount ?? 0) > 0 ? (
        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-900 dark:text-amber-200">
          <strong className="font-medium">Checkout impact:</strong> {target.enabledStoreCount} store
          {target.enabledStoreCount === 1 ? '' : 's'} may lose an online payment option until you reactivate this
          method or merchants use another gateway.
        </p>
      ) : (
        <p>No stores are currently using this method at checkout.</p>
      )}
    </div>
  )
}

export default function PaymentGatewaysPage() {
  const [items, setItems] = useState<PlatformPaymentGatewayRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<PlatformPaymentGatewayRow | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deactivateTarget, setDeactivateTarget] = useState<PlatformPaymentGatewayRow | null>(null)
  const [deactivateMode, setDeactivateMode] = useState<DeactivateMode | null>(null)
  const [deactivating, setDeactivating] = useState(false)

  const refresh = useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const data = await gateways.listPlatformPaymentGateways()
      setItems(data.items || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const usedProviderSet = useMemo(
    () => new Set(items.map((i) => i.provider.trim().toLowerCase())),
    [items],
  )
  const availableProviders = useMemo(
    () => CHECKOUT_PROVIDERS.filter((p) => !usedProviderSet.has(p)),
    [usedProviderSet],
  )

  const effectiveProvider = (editing?.provider ?? form.provider).trim().toLowerCase()
  const currenciesValid = form.supportedCurrencies.length > 0

  function openCreate() {
    setEditing(null)
    setForm({
      ...emptyForm,
      provider: availableProviders[0] ?? emptyForm.provider,
    })
    setModalOpen(true)
  }

  function openEdit(row: PlatformPaymentGatewayRow) {
    setEditing(row)
    setForm({
      displayName: row.displayName,
      provider: row.provider,
      logoUrl: row.logoUrl ?? '',
      sortOrder: String(row.sortOrder ?? 0),
      isGloballyActive: row.isGloballyActive,
      secretKey: '',
      publicKey: '',
      supportedCurrencies: coerceStringArray(row.supportedCurrencies),
    })
    setModalOpen(true)
  }

  function requestDeactivate(row: PlatformPaymentGatewayRow, mode: DeactivateMode) {
    setDeactivateTarget(row)
    setDeactivateMode(mode)
  }

  function cancelDeactivate() {
    if (deactivating || saving) return
    if (deactivateMode === 'edit' && editing?.isGloballyActive) {
      setForm((f) => ({ ...f, isGloballyActive: true }))
    }
    setDeactivateTarget(null)
    setDeactivateMode(null)
  }

  async function persistGateway(payload: Parameters<typeof gateways.patchPlatformPaymentGateway>[1]) {
    if (editing) {
      await gateways.patchPlatformPaymentGateway(editing.id, payload)
    } else {
      await gateways.createPlatformPaymentGateway({
        provider: form.provider.trim(),
        displayName: form.displayName.trim(),
        logoUrl: payload.logoUrl,
        isGloballyActive: payload.isGloballyActive,
        sortOrder: payload.sortOrder,
        supportedCurrencies: payload.supportedCurrencies,
        secretKey: payload.secretKey,
        publicKey: payload.publicKey,
      })
    }
  }

  async function saveForm(overrides?: Partial<Pick<FormState, 'isGloballyActive'>>) {
    const isGloballyActive = overrides?.isGloballyActive ?? form.isGloballyActive
    setSaving(true)
    setError(null)
    const sortOrder = Math.max(0, Math.floor(Number(form.sortOrder) || 0))
    const logoUrl = form.logoUrl.trim() ? form.logoUrl.trim() : null
    const payload: Parameters<typeof gateways.patchPlatformPaymentGateway>[1] = {
      displayName: form.displayName.trim(),
      logoUrl,
      isGloballyActive,
      sortOrder,
      supportedCurrencies: [...form.supportedCurrencies],
    }
    const secretKey = form.secretKey.trim()
    const publicKey = form.publicKey.trim()
    if (secretKey) payload.secretKey = secretKey
    if (publicKey) payload.publicKey = publicKey

    try {
      await persistGateway(payload)
      setModalOpen(false)
      setDeactivateTarget(null)
      setDeactivateMode(null)
      await refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function submit() {
    const validationError = validateForm(form, editing)
    if (validationError) {
      setError(validationError)
      return
    }

    if (editing?.isGloballyActive && !form.isGloballyActive) {
      requestDeactivate(editing, 'edit')
      return
    }

    await saveForm()
  }

  async function confirmDeactivate() {
    if (!deactivateTarget || !deactivateMode) return
    setDeactivating(true)
    setError(null)
    try {
      if (deactivateMode === 'table') {
        await gateways.patchPlatformPaymentGateway(deactivateTarget.id, { isGloballyActive: false })
        setDeactivateTarget(null)
        setDeactivateMode(null)
        setModalOpen(false)
        await refresh()
      } else {
        setForm((f) => ({ ...f, isGloballyActive: false }))
        await saveForm({ isGloballyActive: false })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Deactivate failed')
    } finally {
      setDeactivating(false)
    }
  }

  const initialLoading = loading && items.length === 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Payment methods"
          description="Platform-wide payment partners and the currencies they support at checkout."
          action={
            <Button
              type="button"
              onClick={openCreate}
              disabled={loading || availableProviders.length === 0}
              title={
                availableProviders.length === 0 && !loading
                  ? 'Each payment partner can only be added once'
                  : undefined
              }
            >
              Add method
            </Button>
          }
        />

        {!loading && availableProviders.length === 0 && items.length > 0 ? (
          <p className="mb-4 text-sm text-muted-foreground">
            Every available payment partner is already listed. Edit an existing entry to change settings or
            deactivate it.
          </p>
        ) : null}

        {error ? (
          <p className="mb-4 text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}

        {initialLoading ? (
          <>
            <div className="hidden md:block">
              <SkeletonTable rows={5} columns={9} />
            </div>
            <SkeletonStackedCards count={3} className="md:hidden" />
          </>
        ) : (
          <div className="hidden overflow-x-auto md:block">
            <Table variant="plain">
              <TableHeader>
                <TableRow>
                  <TableHead>S/N</TableHead>
                  <TableHead className="w-14">Logo</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Stores enabled</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead align="right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row, rowIndex) => (
                  <TableRow key={row.id}>
                    <TableCell className="tabular-nums">{rowIndex + 1}</TableCell>
                    <TableCell>
                      {row.logoUrl ? (
                        <img
                          src={row.logoUrl}
                          alt=""
                          className="h-9 w-9 rounded-lg border border-border bg-muted object-contain"
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{row.displayName}</TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{row.slug}</code>
                    </TableCell>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell className="tabular-nums">{row.enabledStoreCount ?? 0}</TableCell>
                    <TableCell>{row.sortOrder}</TableCell>
                    <TableCell>{gatewayStatusBadge(row.isGloballyActive)}</TableCell>
                    <TableCell align="right">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          className="cursor-pointer font-medium text-foreground hover:text-primary"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </button>
                        {row.isGloballyActive ? (
                          <button
                            type="button"
                            className="cursor-pointer font-medium text-destructive hover:text-destructive/80"
                            onClick={() => requestDeactivate(row, 'table')}
                          >
                            Deactivate
                          </button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {!initialLoading && items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No payment methods yet. Add one so shops can offer it at checkout.
          </p>
        ) : null}

        {!initialLoading ? (
          <div className="mt-4 space-y-3 md:hidden">
            {items.map((row, rowIndex) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-semibold tabular-nums text-foreground ring-1 ring-border"
                    aria-label={`S/N ${rowIndex + 1}`}
                  >
                    {rowIndex + 1}
                  </span>
                  {row.logoUrl ? (
                    <img
                      src={row.logoUrl}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain"
                    />
                  ) : null}
                  <div className="min-w-0">
                    <p className="truncate font-medium">{row.displayName}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {row.slug} · {row.provider}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {gatewayStatusBadge(row.isGloballyActive)}
                      <span className="text-[11px] text-muted-foreground">
                        {row.enabledStoreCount ?? 0} store{(row.enabledStoreCount ?? 0) === 1 ? '' : 's'} enabled
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-2">
                  <Button type="button" variant="secondary" size="sm" onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                  {row.isGloballyActive ? (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => requestDeactivate(row, 'table')}
                    >
                      Deactivate
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => {
          if (saving) return
          setModalOpen(false)
        }}
        title={editing ? 'Edit payment method' : 'Add payment method'}
        size="lg"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" disabled={saving} onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={
                saving ||
                !form.displayName.trim() ||
                !currenciesValid ||
                (!editing && availableProviders.length === 0)
              }
              onClick={() => void submit()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {editing ? (
            <div>
              <p className="text-xs font-medium text-muted-foreground">Payment code (can’t be changed)</p>
              <code className="mt-1 block text-sm">{editing.slug}</code>
            </div>
          ) : null}

          <Input
            id="pg-display-name"
            label="Display name"
            value={form.displayName}
            onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            placeholder="Pay with Paystack"
            disabled={saving}
          />

          {!editing ? (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Payment partner</label>
              {availableProviders.length === 0 ? (
                <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  Nothing new to add here. Edit an existing payment method instead.
                </p>
              ) : (
                <select
                  className="h-10 w-full rounded-lg border border-border bg-card px-3 text-sm"
                  value={
                    availableProviders.some((p) => p === form.provider) ? form.provider : (availableProviders[0] ?? '')
                  }
                  onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
                  disabled={saving}
                >
                  {availableProviders.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Must be a payment partner {APP_NAME} already supports. Each partner can only be added once.
              </p>
            </div>
          ) : null}

          <SearchableMultiSelect
            label="Supported currencies *"
            placeholder="Select currencies…"
            searchPlaceholder="Search by name or code…"
            pickerTitle="Supported currencies"
            options={PAYMENT_GATEWAY_CURRENCIES.map((c) => ({ value: c.code, label: c.label }))}
            value={form.supportedCurrencies}
            onChange={(supportedCurrencies) => setForm((f) => ({ ...f, supportedCurrencies }))}
            disabled={saving}
            hint="Checkout offers this gateway only when the store's currency is selected here."
            error={!currenciesValid ? 'Select at least one currency.' : null}
            emptyMessage="No currencies match your search"
          />

          {effectiveProvider === 'paystack' ? (
            <>
              <div>
                <Input
                  id="pg-secret-key"
                  label="Paystack secret key"
                  type="password"
                  autoComplete="off"
                  value={form.secretKey}
                  onChange={(e) => setForm((f) => ({ ...f, secretKey: e.target.value }))}
                  placeholder={
                    editing
                      ? 'Leave blank to keep existing key'
                      : 'Optional — leave blank to use PAYSTACK_SECRET_KEY'
                  }
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Optional. If blank, the server uses PAYSTACK_SECRET_KEY from the environment.
                </p>
              </div>
              <div>
                <Input
                  id="pg-public-key"
                  label="Paystack public key"
                  value={form.publicKey}
                  onChange={(e) => setForm((f) => ({ ...f, publicKey: e.target.value }))}
                  placeholder="pk_live_… or pk_test_…"
                  disabled={saving}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Optional — used for client-side checkout where applicable.
                </p>
              </div>
            </>
          ) : null}

          {effectiveProvider === 'mollie' ? (
            <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Mollie API keys are configured on the server (MOLLIE_API_KEY). No keys are stored on this gateway
              record.
            </p>
          ) : null}

          {effectiveProvider === 'stripe' ? (
            <p className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Stripe keys are configured on the server (STRIPE_SECRET_KEY / STRIPE_PUBLISHABLE_KEY). Typical
              supported currencies for Connect checkout: USD, GBP, EUR. Merchants must complete Stripe Connect
              Express onboarding before Stripe appears at checkout for their store.
            </p>
          ) : null}

          <Input
            id="pg-sort-order"
            label="List order"
            type="number"
            min={0}
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            disabled={saving}
          />

          <FeatureToggle
            label="Available to all stores"
            description="When off, checkout will not offer this payment method."
            checked={form.isGloballyActive}
            onChange={(v) => {
              if (editing?.isGloballyActive && !v) {
                requestDeactivate(editing, 'edit')
                return
              }
              setForm((f) => ({ ...f, isGloballyActive: v }))
            }}
            disabled={saving}
          />

          <ImageUploadField
            label="Logo"
            hint="Optional. Shown to customers at checkout when they pay."
            multiple={false}
            disabled={saving}
            onUploaded={(urls) => {
              if (urls[0]) setForm((f) => ({ ...f, logoUrl: urls[0] }))
            }}
            onUploadError={(msg) => setError(msg)}
          />
          {form.logoUrl ? (
            <div className="flex items-center gap-3">
              <img
                src={form.logoUrl}
                alt=""
                className="h-12 w-12 rounded-lg border border-border object-contain"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => setForm((f) => ({ ...f, logoUrl: '' }))}
              >
                Remove logo
              </Button>
            </div>
          ) : null}
        </div>
      </Modal>

      <Modal
        open={deactivateTarget != null}
        onClose={cancelDeactivate}
        title="Deactivate payment method?"
        size="md"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" disabled={deactivating || saving} onClick={cancelDeactivate}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deactivating || saving}
              onClick={() => void confirmDeactivate()}
            >
              {deactivating || saving
                ? 'Deactivating…'
                : deactivateMode === 'edit'
                  ? 'Save and deactivate'
                  : 'Deactivate'}
            </Button>
          </div>
        }
      >
        {deactivateTarget ? <DeactivateConfirmBody target={deactivateTarget} /> : null}
      </Modal>
    </div>
  )
}
